import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { Branding, PublicOrg } from '../types/branding'
import { brandingApi } from '../services/api/branding'
import { publicOrgApi } from '../services/api/publicOrg'
import { adminApi } from '../services/api/admin'
import { getAccessToken } from '../services/api/client'
import { useAuth } from './AuthContext'
import { useTheme } from './ThemeContext'
import { relativeLuminance } from '../lib/contrast'

function extractSlugFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/o\/([^/]+)\//)
  return match ? match[1] : null
}

interface BrandingContextType {
  branding: Branding | null
  currentLogoUrl: string | null
  loading: boolean
  refresh: () => void
  applyTemporary: (org: PublicOrg) => void
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined)

export const useBranding = () => {
  const context = useContext(BrandingContext)
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider')
  }
  return context
}

function hexToHsl(hex: string): string | null {
  const cleaned = hex.replace('#', '')
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return null
  const red = parseInt(cleaned.slice(0, 2), 16) / 255
  const green = parseInt(cleaned.slice(2, 4), 16) / 255
  const blue = parseInt(cleaned.slice(4, 6), 16) / 255
  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  const lightness = (max + min) / 2
  if (max === min) {
    return `0 0% ${Math.round(lightness * 100)}%`
  }
  const delta = max - min
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min)
  let hue = 0
  if (max === red) {
    hue = ((green - blue) / delta + (green < blue ? 6 : 0)) / 6
  } else if (max === green) {
    hue = ((blue - red) / delta + 2) / 6
  } else {
    hue = ((red - green) / delta + 4) / 6
  }
  return `${Math.round(hue * 360)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`
}

const DARK_FOREGROUND = '222.2 84% 4.9%'
const LIGHT_FOREGROUND = '0 0% 100%'

function applyBrandingCssVars(branding: Branding | null) {
  const root = document.documentElement
  if (!branding || !branding.primaryColor) {
    root.style.removeProperty('--primary')
    root.style.removeProperty('--primary-foreground')
    root.style.removeProperty('--ring')
    return
  }
  const hsl = hexToHsl(branding.primaryColor)
  if (hsl) {
    root.style.setProperty('--primary', hsl)
    root.style.setProperty('--ring', hsl)
    const primaryFg = relativeLuminance(branding.primaryColor) < 0.5 ? LIGHT_FOREGROUND : DARK_FOREGROUND
    root.style.setProperty('--primary-foreground', primaryFg)
  }
}

function applyFavicon(faviconUrl: string | null) {
  const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null
  if (!link) return
  link.href = faviconUrl ?? '/favicon.ico'
}

interface BrandingProviderProps {
  children: ReactNode
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
  const [branding, setBranding] = useState<Branding | null>(null)
  const [loading, setLoading] = useState(false)
  const { resolvedTheme } = useTheme()
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  const hadBrandingRef = useRef(false)
  const temporaryActiveRef = useRef(false)
  const previousPathnameRef = useRef<string>(location.pathname)
  const recordedAccessOrgIdRef = useRef<string | null>(null)

  const fetchBranding = useCallback(async () => {
    if (!getAccessToken()) {
      if (!temporaryActiveRef.current) {
        setBranding(null)
        if (hadBrandingRef.current) {
          applyBrandingCssVars(null)
          hadBrandingRef.current = false
        }
      }
      setLoading(false)
      return
    }
    if (temporaryActiveRef.current) {
      return
    }
    setLoading(true)
    try {
      const data = await brandingApi.getMe()
      if (data) {
        setBranding(data)
        applyBrandingCssVars(data)
        applyFavicon(data.faviconUrl)
        hadBrandingRef.current = true
      } else {
        if (!temporaryActiveRef.current) {
          setBranding(null)
          if (hadBrandingRef.current) {
            applyBrandingCssVars(null)
            hadBrandingRef.current = false
          }
        }
      }
    } catch {
      setBranding(null)
      if (hadBrandingRef.current) {
        applyBrandingCssVars(null)
        hadBrandingRef.current = false
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (isAuthenticated) {
        temporaryActiveRef.current = false
        const urlSlug = extractSlugFromPath(location.pathname)
        const brandedSlug = urlSlug ?? sessionStorage.getItem('branded_slug')
        if (brandedSlug) {
          try {
            const org = await publicOrgApi.getBySlug(brandedSlug)
            if (cancelled) return
            const slugBranding: Branding = {
              orgId: org.id,
              orgName: org.name,
              slug: org.slug,
              primaryColor: org.primaryColor,
              accentColor: org.accentColor,
              logoUrl: org.logoUrl,
              logoDarkUrl: org.logoDarkUrl,
              faviconUrl: org.faviconUrl,
              updatedAt: null,
              updatedBy: null,
            }
            temporaryActiveRef.current = true
            setBranding(slugBranding)
            applyBrandingCssVars(slugBranding)
            applyFavicon(slugBranding.faviconUrl)
            hadBrandingRef.current = true
            if (recordedAccessOrgIdRef.current !== org.id) {
              recordedAccessOrgIdRef.current = org.id
              adminApi.recordAccess(org.id).catch(() => {})
            }
            return
          } catch {
            // fall through to regular fetchBranding
          }
        }
      } else if (isAuthenticated === false) {
        const urlSlug = extractSlugFromPath(location.pathname)
        const pathnameChanged = previousPathnameRef.current !== location.pathname
        if (!urlSlug && temporaryActiveRef.current && pathnameChanged) {
          temporaryActiveRef.current = false
          setBranding(null)
          applyBrandingCssVars(null)
          applyFavicon(null)
          hadBrandingRef.current = false
        }
        recordedAccessOrgIdRef.current = null
      }
      previousPathnameRef.current = location.pathname
      if (!cancelled) fetchBranding()
    }
    run()
    return () => { cancelled = true }
  }, [fetchBranding, isAuthenticated, location.pathname])

  const applyTemporary = useCallback((org: PublicOrg) => {
    const temporary: Branding = {
      orgId: org.id,
      orgName: org.name,
      slug: org.slug,
      primaryColor: org.primaryColor,
      accentColor: org.accentColor,
      logoUrl: org.logoUrl,
      logoDarkUrl: org.logoDarkUrl,
      faviconUrl: org.faviconUrl,
      updatedAt: null,
      updatedBy: null,
    }
    temporaryActiveRef.current = true
    setBranding(temporary)
    applyBrandingCssVars(temporary)
    applyFavicon(temporary.faviconUrl)
  }, [])

  const currentLogoUrl = (() => {
    if (!branding) return null
    if (resolvedTheme === 'dark' && branding.logoDarkUrl) return branding.logoDarkUrl
    return branding.logoUrl
  })()

  const value: BrandingContextType = {
    branding,
    currentLogoUrl,
    loading,
    refresh: fetchBranding,
    applyTemporary,
  }

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  )
}
